import { Button } from "@/components/ui/buttons/Button";
import { IconButton } from "@/components/ui/buttons/IconButton";
import { ConfirmDialog } from "@/components/ui/feedback/ConfirmDialog";
import { FlashAlert } from "@/components/ui/feedback/FlashAlert";
import { StatusMessages } from "@/components/ui/feedback/StatusMessages";
import { Tooltip } from "@/components/ui/feedback/Tooltip";
import { Icon } from "@/components/ui/display/Icon";
import { Select } from "@/components/ui/forms/Select";
import { FilteredList } from "@/components/ui/lists/FilteredList";
import { UserListItem } from "@/components/ui/lists/UserListItem";
import type { SelectOption } from "@/components/ui/forms/Select/types";
import { useAdminUsers } from "@/hooks/data";
import { formatUserName, deriveUserNames } from "@/hooks/data/dataQuality";
import type { IUser } from "@/services";
import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import layoutStyles from "@/components/admin/AdminSectionLayout.module.css";
import styles from "./AdminsSection.module.css";
import type { AdminsSectionProps } from "./types";
import SectionHeader from "@/components/layout/SectionHeader";

/** User-like object for admin list items */
interface AdminUserItem {
  id: number;
  email: string;
  name: string | null;
}

/** Explanation text for admin role - describes actual capabilities */
const ADMIN_EXPLANATION =
  "Administratorer kan endre organisasjonsinnstillinger (timer per dag, fravær), legge til superbrukere og kunder, samt styre hvilke ansatte som er bookbare.";

/**
 * AdminsSection - Manage admin user assignments
 * Part of the Superadmin page
 */
export const AdminsSection: React.FC<AdminsSectionProps> = ({
  className,
  "data-testid": testId = "admins-section",
}) => {
  const {
    admins,
    allUsers,
    nonAdminUsers,
    isLoading,
    error,
    addAdmin,
    removeAdmin,
    isAdding,
    isRemoving,
  } = useAdminUsers();

  // State for add form
  const [selectedUser, setSelectedUser] = useState<SelectOption<IUser>[]>([]);

  // State for delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<AdminUserItem | null>(null);

  // State for flash alerts
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // State for mobile help tooltip
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);

  // Convert non-admin users to select options
  const userOptions: SelectOption<IUser>[] = useMemo(() => {
    return nonAdminUsers.map((user) => {
      const displayName = formatUserName(user);
      return {
        id: user.id,
        label: `${displayName} (${user.email})`,
        data: user,
      };
    });
  }, [nonAdminUsers]);

  // Convert admin emails to user-like objects for UserListItem
  const adminUserItems: AdminUserItem[] = useMemo(() => {
    return admins.map((email, index) => {
      const user = allUsers.find(
        (u) => u.email.toLowerCase() === email.toLowerCase(),
      );
      // Use formatUserName to get proper display name with first_name/last_name fallback
      const displayName = user ? formatUserName(user) : null;
      return {
        id: user?.id ?? index,
        email,
        name: displayName !== email ? displayName : null,
      };
    });
  }, [admins, allUsers]);

  const renderUserOption = useCallback((option: SelectOption<IUser>) => {
    const displayName = option.data
      ? formatUserName(option.data)
      : option.label;
    return (
      <div className={styles.userSelectCard}>
        <div className={styles.userSelectName}>{displayName}</div>
        <div className={styles.userSelectEmail}>
          {option.data?.email || option.label}
        </div>
      </div>
    );
  }, []);

  // Render selected value with styled name and email
  const renderUserValue = useCallback((option: SelectOption<IUser>) => {
    const { displayName } = option.data
      ? deriveUserNames(option.data)
      : { displayName: option.label };
    return (
      <div className={styles.userSelectValue}>
        <span className={styles.userSelectValueName}>{displayName}</span>
        <span className={styles.userSelectValueEmail}>
          {option.data?.email}
        </span>
      </div>
    );
  }, []);

  // Handle add admin
  const handleAdd = useCallback(async () => {
    if (selectedUser.length === 0 || !selectedUser[0].data) return;

    const email = selectedUser[0].data.email;
    const success = await addAdmin(email);

    if (success) {
      setSuccessMessage("Administrator lagt til");
      setSelectedUser([]);
    } else {
      setErrorMessage("Kunne ikke legge til administrator");
    }
  }, [selectedUser, addAdmin]);

  // Handle delete confirmation
  const handleDeleteClick = useCallback((admin: AdminUserItem) => {
    setDeleteTarget(admin);
  }, []);

  // Handle delete confirm
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;

    const success = await removeAdmin(deleteTarget.email);

    if (success) {
      setSuccessMessage("Administrator fjernet");
    } else {
      setErrorMessage("Kunne ikke fjerne administrator");
    }

    setDeleteTarget(null);
  }, [deleteTarget, removeAdmin]);

  // Handle delete cancel
  const handleDeleteCancel = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  // Render admin list item using UserListItem for consistency with SuperusersSection
  const renderAdminItem = useCallback(
    (admin: AdminUserItem) => (
      <UserListItem
        key={admin.email}
        user={admin}
        onDelete={handleDeleteClick}
        isDeleting={isRemoving}
        deleteLabel="Fjern"
        className={styles.listItem}
        data-testid={`${testId}-item-${admin.email}`}
      />
    ),
    [handleDeleteClick, isRemoving, testId],
  );

  return (
    <div
      className={classNames(layoutStyles.section, styles.section, className)}
      data-testid={testId}
    >
      {/* Explanation text - visible only on desktop */}
      <p className={layoutStyles.explanationText}>{ADMIN_EXPLANATION}</p>

      {/* Flash alerts */}
      {successMessage && (
        <FlashAlert
          alert={{ message: successMessage, variant: "success" }}
          onDismiss={() => setSuccessMessage(null)}
          data-testid={`${testId}-success-alert`}
        />
      )}
      {errorMessage && (
        <FlashAlert
          alert={{ message: errorMessage, variant: "error" }}
          onDismiss={() => setErrorMessage(null)}
          data-testid={`${testId}-error-alert`}
        />
      )}

      {/* Add admin form */}
      <div className={layoutStyles.dataAreaGrid}>
        <section className={layoutStyles.card} data-area="admin-search">
          <SectionHeader
            as="h2"
            size="md"
            action={
              <div className={styles.helpButtonWrapper}>
                <Tooltip
                  content={ADMIN_EXPLANATION}
                  position="left"
                  width="md"
                  show={showHelpTooltip}
                >
                  <IconButton
                    icon={<Icon name="help-circle" size="sm" aria-hidden />}
                    variant="default"
                    size="sm"
                    onClick={() => setShowHelpTooltip((prev) => !prev)}
                    aria-label="Hva er en administrator?"
                    className={styles.helpButton}
                    data-testid={`${testId}-help-button`}
                  />
                </Tooltip>
              </div>
            }
          >
            Legg til administrator
          </SectionHeader>
          <StatusMessages
            error={error}
            isLoading={isLoading}
            errorLabel="Kunne ikke laste administratorer"
            loadingLabel="Laster administratorer..."
            testId={testId}
          />

          {!isLoading && !error && (
            <div className={styles.addForm} data-testid={`${testId}-add-form`}>
              <div className={styles.formField}>
                <label htmlFor="admin-select" className={styles.fieldLabel}>
                  Velg bruker
                </label>
                <div className={styles.selectWrapper}>
                  <Select<IUser>
                    id="admin-select"
                    appearance="clean"
                    placeholder="Søk etter navn eller e-post..."
                    size="md"
                    options={userOptions}
                    value={selectedUser}
                    onChange={setSelectedUser}
                    searchable
                    disabled={isAdding}
                    data-testid={`${testId}-user-select`}
                    renderOption={renderUserOption}
                    renderValue={renderUserValue}
                  />
                </div>
              </div>

              <div className={styles.buttonRow}>
                <Button
                  onClick={handleAdd}
                  disabled={selectedUser.length === 0}
                  isLoading={isAdding}
                  loadingText="Legger til..."
                  className={styles.addButton}
                  data-testid={`${testId}-add-button`}
                >
                  Legg til administrator
                </Button>
              </div>
            </div>
          )}
        </section>
        <section className={layoutStyles.card} data-area="admin-list">
          {/* Admins list */}
          {!isLoading && !error && (
            <>
              <SectionHeader as="h2" size="md">
                Liste over administratorer
              </SectionHeader>
              <FilteredList
                items={adminUserItems}
                filterItem={(admin, normalized) => {
                  const searchFields = [admin.name, admin.email];
                  return searchFields
                    .filter(Boolean)
                    .some((value) => value!.toLowerCase().includes(normalized));
                }}
                renderItem={renderAdminItem}
                placeholder="Søk etter navn eller e-post"
                debounce={150}
                searchDisabled={isLoading}
                className={styles.listWrapper}
                maxItems={6}
                itemHeight={58}
                data-testid={`${testId}-filtered-list`}
              />
            </>
          )}
        </section>
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Fjern administrator"
        text={`Er du sikker på at du vil fjerne ${deleteTarget?.name ?? deleteTarget?.email} som administrator?`}
        confirmText="Fjern"
        cancelText="Avbryt"
        destructive
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        data-testid={`${testId}-delete-dialog`}
      />
    </div>
  );
};

AdminsSection.displayName = "AdminsSection";

export default AdminsSection;
